import { useCallback, useEffect, useRef, useState } from "react";
import { FaFilePdf } from "react-icons/fa6";
import { IoClose, IoNotificationsOffOutline, IoNotificationsOutline } from "react-icons/io5";
import { MdOutlineImage, MdOutlineInsertDriveFile, MdOutlinePeople } from "react-icons/md";
import { useSelector } from "react-redux";
import {
  PANEL_PAGE_SIZE,
  webChatPanelDetailsPath,
  webChatPanelDocumentsPath,
  webChatPanelMediaPath,
  webChatPanelUsersPath,
} from "../../constants/chatApiPaths";
import PdfViewerModal, { pdfFileNameFrom } from "../../components/PdfViewerModal";
import Api from "../../service/http";

/** Max height of each panel section scroll area (media, documents, members), matching mobile. */
const PANEL_INNER_MAX_PX = 400;

const Details = ({ chat, setChat, showDetails, setShowDetails }) => {
  const { theme } = useSelector((state) => state.theme);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const mediaScrollRef = useRef(null);
  const docsScrollRef = useRef(null);
  const usersScrollRef = useRef(null);
  const usersSentinelRef = useRef(null);
  const mediaSentinelRef = useRef(null);
  const docsSentinelRef = useRef(null);

  const [panelSummary, setPanelSummary] = useState(null);

  const [members, setMembers] = useState([]);
  const [userPage, setUserPage] = useState(1);
  const [usersHasMore, setUsersHasMore] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);

  const [mediaMessages, setMediaMessages] = useState([]);
  const [mediaPage, setMediaPage] = useState(1);
  const [mediaHasMore, setMediaHasMore] = useState(true);
  const [mediaLoading, setMediaLoading] = useState(false);

  const [documentItems, setDocumentItems] = useState([]);
  const [docPage, setDocPage] = useState(1);
  const [docsHasMore, setDocsHasMore] = useState(true);
  const [docsLoading, setDocsLoading] = useState(false);

  const [panelBooting, setPanelBooting] = useState(false);
  const [pdfViewer, setPdfViewer] = useState(null);

  const loadMoreUsers = useCallback(async () => {
    if (!chat?.id || usersLoading || !usersHasMore || panelBooting) return;
    setUsersLoading(true);
    try {
      const nextPage = userPage + 1;
      const res = await Api.get(
        `${webChatPanelUsersPath(chat.id)}?page=${nextPage}&per_page=${PANEL_PAGE_SIZE}`
      );
      const rows = res.data?.data ?? [];
      const meta = res.data?.meta;
      setMembers((prev) => [...prev, ...rows]);
      setUserPage(nextPage);
      setUsersHasMore(meta?.has_more ?? false);
    } catch (e) {
      console.error("Panel users:", e);
    } finally {
      setUsersLoading(false);
    }
  }, [chat?.id, userPage, usersLoading, usersHasMore, panelBooting]);

  const loadMoreMedia = useCallback(async () => {
    if (!chat?.id || mediaLoading || !mediaHasMore || panelBooting) return;
    setMediaLoading(true);
    try {
      const nextPage = mediaPage + 1;
      const res = await Api.get(
        `${webChatPanelMediaPath(chat.id)}?page=${nextPage}&per_page=${PANEL_PAGE_SIZE}`
      );
      const rows = res.data?.data ?? [];
      const meta = res.data?.meta;
      setMediaMessages((prev) => [...prev, ...rows]);
      setMediaPage(nextPage);
      setMediaHasMore(meta?.has_more ?? false);
    } catch (e) {
      console.error("Panel media:", e);
    } finally {
      setMediaLoading(false);
    }
  }, [chat?.id, mediaPage, mediaLoading, mediaHasMore, panelBooting]);

  const loadMoreDocuments = useCallback(async () => {
    if (!chat?.id || docsLoading || !docsHasMore || panelBooting) return;
    setDocsLoading(true);
    try {
      const nextPage = docPage + 1;
      const res = await Api.get(
        `${webChatPanelDocumentsPath(chat.id)}?page=${nextPage}&per_page=${PANEL_PAGE_SIZE}`
      );
      const rows = res.data?.data ?? [];
      const meta = res.data?.meta;
      setDocumentItems((prev) => [...prev, ...rows]);
      setDocPage(nextPage);
      setDocsHasMore(meta?.has_more ?? false);
    } catch (e) {
      console.error("Panel documents:", e);
    } finally {
      setDocsLoading(false);
    }
  }, [chat?.id, docPage, docsLoading, docsHasMore, panelBooting]);

  useEffect(() => {
    if (!showDetails || !chat?.id) return;
    let cancelled = false;
    (async () => {
      setPanelBooting(true);
      setMembers([]);
      setMediaMessages([]);
      setDocumentItems([]);
      setUserPage(1);
      setMediaPage(1);
      setDocPage(1);
      setUsersHasMore(true);
      setMediaHasMore(true);
      setDocsHasMore(true);
      try {
        const [dRes, uRes, mRes, docRes] = await Promise.all([
          Api.get(webChatPanelDetailsPath(chat.id)),
          Api.get(`${webChatPanelUsersPath(chat.id)}?page=1&per_page=${PANEL_PAGE_SIZE}`),
          Api.get(`${webChatPanelMediaPath(chat.id)}?page=1&per_page=${PANEL_PAGE_SIZE}`),
          Api.get(`${webChatPanelDocumentsPath(chat.id)}?page=1&per_page=${PANEL_PAGE_SIZE}`),
        ]);
        if (cancelled) return;
        if (dRes.data?.data) setPanelSummary(dRes.data.data);
        const uRows = uRes.data?.data ?? [];
        setMembers(uRows);
        setUserPage(1);
        setUsersHasMore(uRes.data?.meta?.has_more ?? false);
        const mRows = mRes.data?.data ?? [];
        setMediaMessages(mRows);
        setMediaPage(1);
        setMediaHasMore(mRes.data?.meta?.has_more ?? false);
        const dRows = docRes.data?.data ?? [];
        setDocumentItems(dRows);
        setDocPage(1);
        setDocsHasMore(docRes.data?.meta?.has_more ?? false);
      } catch (e) {
        console.error("Error loading chat panel:", e);
      } finally {
        if (!cancelled) setPanelBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showDetails, chat?.id]);

  useEffect(() => {
    if (!showDetails || panelBooting) return;

    const watch = (root, sentinel, onIntersect) => {
      if (!root || !sentinel) return () => {};
      const io = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) onIntersect();
        },
        { root, rootMargin: "80px", threshold: 0 }
      );
      io.observe(sentinel);
      return () => io.disconnect();
    };

    const c1 = watch(mediaScrollRef.current, mediaSentinelRef.current, () => loadMoreMedia());
    const c2 = watch(docsScrollRef.current, docsSentinelRef.current, () => loadMoreDocuments());
    const c3 = watch(usersScrollRef.current, usersSentinelRef.current, () => loadMoreUsers());
    return () => {
      c1();
      c2();
      c3();
    };
  }, [showDetails, panelBooting, loadMoreUsers, loadMoreMedia, loadMoreDocuments, members.length, mediaMessages.length, documentItems.length]);

  const handleImageClick = (image) => {
    setSelectedMessage(image);
    setIsOpen(true);
  };

  const handleClose = () => {
    setSelectedMessage(null);
    setIsOpen(false);
  };

  const [isZoomed, setIsZoomed] = useState(false);

  const handleDoubleClick = () => {
    setIsZoomed((prev) => !prev);
  };

  const displayName = panelSummary?.name ?? chat?.name;
  const displaySubtitle =
    panelSummary?.member_count != null
      ? `${panelSummary.member_count} members`
      : "Member";

  const mediaFallback =
    chat?.messages
      ?.filter((message) => ["IMGS", "IMG", "VID"].includes(message?.message_type))
      ?.reverse() ?? [];
  const documentsFallback =
    chat?.messages
      ?.filter(
        (message) => !["IMG", "TXT", "VID", "IMGS", "AUD"].includes(message?.message_type) && message?.media?.file
      )
      ?.reverse() ?? [];

  const displayMedia = mediaMessages.length > 0 ? mediaMessages : !panelBooting ? mediaFallback : [];
  const displayDocuments =
    documentItems.length > 0 ? documentItems : !panelBooting ? documentsFallback : [];
  const displayMembers = members.length > 0 ? members : !panelBooting ? chat?.users ?? [] : [];

  const toggleNotifications = async () => {
    await Api.get(`/web/chat/${chat?.id}/${!chat?.mute ? "mute" : "unmute"}`);
    setChat((prevChat) => ({
      ...prevChat,
      mute: !prevChat?.mute,
    }));
  };

  return (
    <aside
      className={`${
        showDetails
          ? "fixed inset-0 z-50 lg:static lg:w-[300px]"
          : "fixed inset-y-0 right-0 w-0 opacity-0 lg:static lg:w-0 lg:opacity-100 overflow-hidden border-l-0 lg:border-l-0"
      } shrink-0 h-full flex flex-col items-center lg:items-stretch border-l transition-all duration-300 ease-in-out ${
        theme === "dark" ? "bg-[#232634] border-gray-700" : "bg-white border-gray-100"
      }`}
    >
      <div className="relative w-full max-w-[400px] lg:w-[300px] flex-1 flex flex-col min-h-0 overflow-y-auto custom-scrollbar p-6 pt-10">
        <div className="flex justify-end lg:hidden absolute top-4 right-4">
          <button
            onClick={() => showDetails && typeof setShowDetails === "function" && setShowDetails(false)}
            className={`p-2 rounded-full ${
              theme === "dark" ? "hover:bg-gray-700 text-gray-200" : "hover:bg-gray-100 text-gray-800"
            }`}
          >
            <IoClose size={24} />
          </button>
        </div>
        <div className="flex flex-col items-center text-center mb-8 shrink-0">
          <div className="relative mb-4">
            <img
              src={panelSummary?.image || chat?.image || "https://placehold.co/400"}
              className="w-20 h-20 rounded-full object-cover shadow-md"
              alt="profile"
            />
            <div
              className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 ${
                theme === "dark" ? "border-[#2f3349]" : "border-white"
              } bg-green-500`}
            ></div>
          </div>
          <h4
            className={`text-lg font-medium mb-1 ${
              theme === "dark" ? "text-gray-200" : "text-gray-800"
            }`}
          >
            {displayName}
          </h4>
          <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>{displaySubtitle}</p>
        </div>

        <div className="flex justify-center gap-4 mb-8 shrink-0">
          <button onClick={toggleNotifications} className="flex flex-col items-center gap-1 group">
            <div
              className={`p-2 rounded-lg transition-all ${
                chat?.mute
                  ? "bg-red-50 text-red-500"
                  : theme === "dark"
                    ? "bg-gray-800 text-gray-400"
                    : "bg-gray-50 text-gray-500"
              } group-hover:bg-indigo-50 group-hover:text-indigo-600`}
            >
              {chat?.mute ? <IoNotificationsOffOutline size={20} /> : <IoNotificationsOutline size={20} />}
            </div>
            <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 group-hover:text-indigo-600">
              Mute
            </span>
          </button>
        </div>

        <div className="space-y-6 min-h-0">
          <div>
            <div className="flex items-center gap-2 mb-3 text-gray-400">
              <MdOutlineImage size={18} />
              <span className="text-xs font-bold uppercase tracking-widest">Media</span>
            </div>
            <div
              ref={mediaScrollRef}
              style={{ maxHeight: PANEL_INNER_MAX_PX }}
              className="min-h-0 overflow-y-auto custom-scrollbar rounded-lg pr-0.5"
            >
              <div className="grid grid-cols-3 gap-2">
                {panelBooting && mediaMessages.length === 0 ? (
                  <p className={`text-xs col-span-3 ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}>
                    Loading…
                  </p>
                ) : (
                  displayMedia.flatMap((message, index) => {
                    if (message?.message_type === "IMGS") {
                      return (message?.media ?? []).map((item, i) => (
                        <img
                          key={`${message.id ?? "m"}-${i}`}
                          src={item?.file || "https://placehold.co/100"}
                          alt="media"
                          className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity shadow-sm"
                          onClick={() => handleImageClick(message)}
                        />
                      ));
                    }
                    if (message?.message_type === "IMG") {
                      return [
                        <img
                          key={message?.id ?? `img-${index}`}
                          src={message?.media?.file || "https://placehold.co/100"}
                          alt="media"
                          className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity shadow-sm"
                          onClick={() => handleImageClick(message)}
                        />,
                      ];
                    }
                    return [
                      <div
                        key={message?.id ?? `vid-${index}`}
                        className="relative w-full h-24 rounded-lg overflow-hidden group cursor-pointer"
                        onClick={() => handleImageClick(message)}
                      >
                        <video
                          src={message?.media?.file}
                          className="w-full h-full object-cover"
                          muted
                          preload="metadata"
                        />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white">
                            <div className="w-0 h-0 border-t-4 border-t-transparent border-l-8 border-l-white border-b-4 border-b-transparent ml-1"></div>
                          </div>
                        </div>
                      </div>,
                    ];
                  })
                )}
              </div>
              {mediaLoading && <p className="text-[10px] text-center text-gray-400 mt-2">Loading more…</p>}
              <div ref={mediaSentinelRef} className="h-1 w-full" aria-hidden="true" />
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3 text-gray-400">
              <MdOutlineInsertDriveFile size={18} />
              <span className="text-xs font-bold uppercase tracking-widest">Documents</span>
            </div>
            <div
              ref={docsScrollRef}
              style={{ maxHeight: PANEL_INNER_MAX_PX }}
              className="min-h-0 overflow-y-auto custom-scrollbar space-y-2 rounded-lg pr-0.5"
            >
              {panelBooting && documentItems.length === 0 ? (
                <p className={`text-xs ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}>Loading…</p>
              ) : (
                displayDocuments.map((message, index) => {
                  const fileUrl = message?.media?.file ?? message?.file;
                  const name =
                    message?.media?.name ?? message?.name ?? pdfFileNameFrom(fileUrl);
                  // The panel lists every non-media attachment, so only route the
                  // PDFs to the in-app viewer and leave other types as downloads.
                  const isPdf =
                    message?.message_type === "PDF" ||
                    (typeof fileUrl === "string" && fileUrl.split("?")[0].toLowerCase().endsWith(".pdf"));

                  const rowClass = `flex items-center gap-3 p-2 rounded-lg border transition-all w-full text-left ${
                    theme === "dark" ? "border-gray-700 hover:bg-gray-800" : "border-gray-100 hover:bg-gray-50"
                  }`;

                  const body = (
                    <>
                      <div
                        className={`w-8 h-8 rounded flex items-center justify-center shrink-0 ${
                          isPdf ? "bg-red-50 text-red-500" : "bg-indigo-50 text-indigo-500"
                        }`}
                      >
                        {isPdf ? <FaFilePdf size={16} /> : <MdOutlineInsertDriveFile size={18} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-xs font-medium truncate ${
                            theme === "dark" ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {name || "Document"}
                        </p>
                        <p className="text-[10px] text-gray-400">{isPdf ? "PDF" : "File"}</p>
                      </div>
                    </>
                  );

                  return isPdf ? (
                    <button
                      key={message?.id ?? `doc-${index}`}
                      type="button"
                      onClick={() => setPdfViewer({ url: fileUrl, title: name || "Document" })}
                      className={rowClass}
                    >
                      {body}
                    </button>
                  ) : (
                    <a
                      key={message?.id ?? `doc-${index}`}
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={rowClass}
                    >
                      {body}
                    </a>
                  );
                })
              )}
              {docsLoading && <p className="text-[10px] text-center text-gray-400 py-1">Loading more…</p>}
              <div ref={docsSentinelRef} className="h-1 w-full" aria-hidden="true" />
            </div>
          </div>

          {chat?.id && (
            <div>
              <div className="flex items-center gap-2 mb-3 text-gray-400">
                <MdOutlinePeople size={18} />
                <span className="text-xs font-bold uppercase tracking-widest">Members</span>
              </div>
              <div
                ref={usersScrollRef}
                style={{ maxHeight: PANEL_INNER_MAX_PX }}
                className="min-h-0 overflow-y-auto custom-scrollbar space-y-3 rounded-lg pr-0.5"
              >
                {displayMembers.map((user, index) => (
                  <div key={`${user?.id ?? "u"}-${index}`} className="flex items-center gap-3">
                    <img
                      src={user?.dp ?? "https://placehold.co/400"}
                      alt="User"
                      className="w-8 h-8 rounded-full border border-gray-100 shadow-sm"
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-xs font-medium ${
                          theme === "dark" ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        {user?.first_name || "User"}
                      </p>
                    </div>
                  </div>
                ))}
                {!panelBooting && displayMembers.length === 0 && (
                  <p className={`text-xs ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}>
                    No members to show
                  </p>
                )}
                {usersLoading && <p className="text-[10px] text-center text-gray-400 py-1">Loading more…</p>}
                <div ref={usersSentinelRef} className="h-1 w-full" aria-hidden="true" />
              </div>
            </div>
          )}
        </div>
      </div>

      {isOpen && (
        <div
          onClick={(e) => {
            if (!e.target.closest(".modal-content")) handleClose();
          }}
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100] p-4 backdrop-blur-sm"
        >
          <div className="relative max-w-5xl w-full flex flex-col items-center">
            <button
              onClick={handleClose}
              className="absolute -top-12 right-0 text-white hover:text-indigo-400 transition-colors"
            >
              <IoClose size={32} />
            </button>

            {selectedMessage?.message_type === "IMG" || selectedMessage?.message_type === "IMGS" ? (
              <img
                src={
                  (selectedMessage?.message_type === "IMGS"
                    ? selectedMessage?.media?.[0]?.file
                    : selectedMessage?.media?.file) || "https://placehold.co/400"
                }
                alt="Zoomed"
                onDoubleClick={handleDoubleClick}
                className={`modal-content max-h-[85vh] object-contain rounded-lg shadow-2xl transition-transform duration-300 ${
                  isZoomed ? "scale-150" : "scale-100"
                }`}
              />
            ) : (
              <video
                src={selectedMessage?.media?.file}
                controls
                autoPlay
                className="modal-content max-h-[85vh] w-full rounded-lg shadow-2xl"
              />
            )}
          </div>
        </div>
      )}

      {pdfViewer && (
        <PdfViewerModal
          url={pdfViewer.url}
          title={pdfViewer.title}
          onClose={() => setPdfViewer(null)}
        />
      )}
    </aside>
  );
};

export default Details;
